import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Camera, Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useBrand } from "../contexts/BrandContext";
import { api } from "../lib/api";

function brandLink(to) {
  const params = new URLSearchParams(window.location.search);
  const brand = params.get("brand");
  if (brand) {
    const sep = to.includes("?") ? "&" : "?";
    return `${to}${sep}brand=${brand}`;
  }
  return to;
}

const SUPPORT_ITEMS = [
  { to: "/teknik-servis", label: "Teknik Servis" },
  { to: "/kullanim-kilavuzlari", label: "Kullanım Kılavuzları" },
  { to: "/destek/firmware", label: "Firmware" },
  { to: "/destek/kalibrasyon", label: "Kalibrasyon Dosyaları" },
  { to: "/destek/yazilimlar", label: "Yazılımlar & Sürücüler" },
];

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/urunler", label: "Ürünler" },
  { to: "/bayiler", label: "Bayiler" },
  { label: "Destek", dropdown: SUPPORT_ITEMS },
  { to: "/blog", label: "Blog" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/iletisim", label: "İletişim" },
];

const FOOTER_LINKS = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/urunler", label: "Ürünler" },
  { to: "/bayiler", label: "Bayiler" },
  { to: "/blog", label: "Blog" },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/iletisim", label: "İletişim" },
];

export default function PublicLayout() {
  const { mode, brand, loading } = useBrand();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    const slug = mode === "brand" ? brand.slug : "_all";
    api.get(`/settings-resolved/${slug}`).then((r) => setSettings(r.data || {})).catch(() => setSettings({}));
  }, [loading, mode, brand]);

  useEffect(() => {
    if (settings.favicon) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = settings.favicon;
    }
    if (settings.site_title) {
      document.title = settings.site_title;
    } else if (mode === "brand" && brand) {
      document.title = `${brand.name} — Noon Foto`;
    } else {
      document.title = "Noon Foto";
    }
  }, [settings, mode, brand]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500">
        <div className="font-mono text-xs tracking-widest">YÜKLENİYOR...</div>
      </div>
    );
  }

  // Logo precedence: site_settings.logo > brand.logo > icon fallback
  const logo = settings.logo || brand?.logo || "";
  const siteName = settings.site_title || (mode === "brand" ? brand.name : "NOON FOTO");
  const description = settings.description || (mode === "brand"
    ? "Türkiye Resmi Distribütörü: Noon Foto"
    : "Profesyonel fotoğraf ekipmanlarının Türkiye distribütörü");

  const socials = [
    { key: "instagram", icon: Instagram, url: settings.instagram },
    { key: "facebook", icon: Facebook, url: settings.facebook },
    { key: "youtube", icon: Youtube, url: settings.youtube },
    { key: "linkedin", icon: Linkedin, url: settings.linkedin },
  ].filter((s) => s.url);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <header
        className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={brandLink("/")} className="flex items-center gap-3" data-testid="header-logo">
              {logo ? (
                <img src={logo} alt={siteName} className="h-8 w-auto object-contain" data-testid="header-logo-img" />
              ) : (
                <Camera className="h-6 w-6" style={{ color: "var(--brand-primary)" }} />
              )}
              <div className="leading-tight">
                <div className="font-display text-base font-semibold tracking-tight">{siteName}</div>
                {mode === "brand" && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Türkiye Resmi Distribütörü: Noon Foto
                  </div>
                )}
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) =>
                n.dropdown ? (
                  <DropdownNav key={n.label} item={n} />
                ) : (
                  <NavLink
                    key={n.to}
                    to={brandLink(n.to)}
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm transition-colors ${
                        location.pathname === n.to || isActive
                          ? "text-white"
                          : "text-zinc-400 hover:text-white"
                      }`
                    }
                    data-testid={`nav-${n.to.replace(/\//g, "-") || "home"}`}
                  >
                    {n.label}
                  </NavLink>
                )
              )}
            </nav>

            <button
              className="lg:hidden p-2 text-zinc-300"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-black/95" data-testid="mobile-menu">
            <div className="px-4 py-2 flex flex-col">
              {NAV.map((n) =>
                n.dropdown ? (
                  <div key={n.label}>
                    <div className="py-3 text-xs uppercase tracking-[0.2em] text-zinc-500 border-b border-white/5">{n.label}</div>
                    {n.dropdown.map((s) => (
                      <Link
                        key={s.to}
                        to={brandLink(s.to)}
                        onClick={() => setOpen(false)}
                        className="py-3 pl-4 text-sm text-zinc-400 hover:text-white border-b border-white/5"
                        data-testid={`mobile-nav-${s.to.replace(/\//g, "-")}`}
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={n.to}
                    to={brandLink(n.to)}
                    onClick={() => setOpen(false)}
                    className="py-3 text-sm text-zinc-300 hover:text-white border-b border-white/5"
                    data-testid={`mobile-nav-${n.to.replace(/\//g, "-") || "home"}`}
                  >
                    {n.label}
                  </Link>
                )
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-[#050505] mt-20" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand block */}
          <div className="md:col-span-1">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 w-auto object-contain mb-4" data-testid="footer-logo" />
            ) : (
              <div className="font-display text-lg font-semibold mb-2">{siteName}</div>
            )}
            <div className="text-sm text-zinc-400 leading-relaxed" data-testid="footer-description">
              {description}
            </div>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-5" data-testid="footer-socials">
                {socials.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 border border-white/10 hover:border-white/40 hover:bg-white/5 flex items-center justify-center transition-colors"
                    aria-label={s.key}
                    data-testid={`footer-social-${s.key}`}
                  >
                    <s.icon size={15} className="text-zinc-300" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Bağlantılar</div>
            <ul className="space-y-2 text-sm">
              {FOOTER_LINKS.map((n) => (
                <li key={n.to}>
                  <Link to={brandLink(n.to)} className="text-zinc-400 hover:text-white transition-colors" data-testid={`footer-link-${n.to.replace(/\//g, "-") || "home"}`}>
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support links */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Destek</div>
            <ul className="space-y-2 text-sm">
              {SUPPORT_ITEMS.map((s) => (
                <li key={s.to}>
                  <Link to={brandLink(s.to)} className="text-zinc-400 hover:text-white transition-colors">
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">İletişim</div>
            <div className="space-y-3 text-sm">
              {settings.address && (
                <div className="flex items-start gap-2 text-zinc-400" data-testid="footer-address">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap">{settings.address}</span>
                </div>
              )}
              {settings.phone && (
                <div className="flex items-center gap-2 text-zinc-400" data-testid="footer-phone">
                  <Phone size={14} className="shrink-0" />
                  <a href={`tel:${settings.phone}`} className="hover:text-white">{settings.phone}</a>
                </div>
              )}
              {settings.email && (
                <div className="flex items-center gap-2 text-zinc-400" data-testid="footer-email">
                  <Mail size={14} className="shrink-0" />
                  <a href={`mailto:${settings.email}`} className="hover:text-white">{settings.email}</a>
                </div>
              )}
              {!settings.address && !settings.phone && !settings.email && (
                <div className="text-zinc-500 text-xs">İletişim bilgileri admin panelden ayarlanabilir.</div>
              )}
            </div>
          </div>
        </div>
        <div className="border-t border-white/5 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-xs text-zinc-600">
            <div>© {new Date().getFullYear()} {siteName}. Tüm hakları saklıdır.</div>
            <Link to="/admin/login" className="hover:text-zinc-300" data-testid="admin-link">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { brandLink };

function DropdownNav({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
    >
      <button
        type="button"
        className="px-3 py-2 text-sm text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-1"
        onClick={() => setOpen((o) => !o)}
      >
        {item.label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50 min-w-[220px]">
          <div className="bg-[#0A0A0A] border border-white/10 backdrop-blur-xl shadow-2xl">
            {item.dropdown.map((s) => (
              <Link
                key={s.to}
                to={brandLink(s.to)}
                className="block px-4 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/5 border-b border-white/5 last:border-b-0"
                onClick={() => setOpen(false)}
                data-testid={`dropdown-link-${s.to.replace(/\//g, "-")}`}
              >
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
